import { useState, type ReactNode } from 'react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetDutyCosts } from '~/api/logistics';
import { Button } from '~/components/ui/buttons/Button';
import { FilterSidebar } from '~/components/ui/FilterSidebar';
import { Input } from '~/components/ui/inputs/Input';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import {
  Table,
  type TableColumn,
  type TableRow,
  useTable,
} from '~/components/ui/table';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';

function formatRate(rate: number | null | undefined, suffix = '%'): string {
  if (rate == null) return '—';
  return `${rate}${suffix}`;
}

function ActiveBadge({
  active,
  label,
}: {
  active: boolean;
  label: string;
}): ReactNode {
  return (
    <span
      className={`text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap ${
        active ? 'text-success-700 bg-success-100' : 'text-neut-500 bg-neut-100'
      }`}
    >
      {label}
    </span>
  );
}

function FlagBadge({ value }: { value: boolean }): ReactNode {
  return value ? (
    <span className="text-xs font-medium px-2 py-1 rounded-lg bg-prim-50 text-prim whitespace-nowrap">
      Yes
    </span>
  ) : (
    <span className="text-xs text-neut-400">—</span>
  );
}

export function DutyCostsTable() {
  const { t } = useTranslation('logistics');

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingOrigin, setPendingOrigin] = useState('');
  const [pendingActive, setPendingActive] = useState('true');

  const { data, isLoading } = useGetDutyCosts();
  const allDutyCosts = data?.dutyCosts ?? [];

  const filtered = allDutyCosts.filter((d) => {
    if (filterActive !== '' && String(d.active) !== filterActive) return false;
    if (
      filterOrigin &&
      !d.originCountryCode?.toLowerCase().includes(filterOrigin.toLowerCase())
    )
      return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (
        !d.htsNumber.toLowerCase().includes(q) &&
        !d.description?.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const activeFilterCount = [
    filterSearch !== '',
    filterOrigin !== '',
    filterActive !== 'true',
  ].filter(Boolean).length;

  const tableControls = useTable({});

  const applyFilter = () => {
    setFilterSearch(pendingSearch);
    setFilterOrigin(pendingOrigin);
    setFilterActive(pendingActive);
    setFilterPanelOpen(false);
  };

  const clearFilter = () => {
    setPendingSearch('');
    setPendingOrigin('');
    setPendingActive('true');
    setFilterSearch('');
    setFilterOrigin('');
    setFilterActive('true');
  };

  const columns: TableColumn[] = [
    {
      key: 'htsNumber',
      header: t('logistics.duty_costs.table.hts_number'),
      width: 8,
    },
    {
      key: 'description',
      header: t('logistics.duty_costs.table.description'),
      width: 14,
    },
    {
      key: 'origin',
      header: t('logistics.duty_costs.table.origin'),
      width: 5,
    },
    {
      key: 'adValoremRate',
      header: t('logistics.duty_costs.table.ad_valorem_rate'),
      width: 7,
    },
    {
      key: 'specificRate',
      header: t('logistics.duty_costs.table.specific_rate'),
      width: 7,
    },
    {
      key: 'section301Rate',
      header: t('logistics.duty_costs.table.section_301_rate'),
      width: 7,
    },
    {
      key: 'adCvdRate',
      header: t('logistics.duty_costs.table.ad_cvd_rate'),
      width: 7,
    },
    {
      key: 'gspEligible',
      header: t('logistics.duty_costs.table.gsp_eligible'),
      width: 5,
    },
    {
      key: 'conditional',
      header: t('logistics.duty_costs.table.conditional'),
      width: 5,
    },
    {
      key: 'active',
      header: t('logistics.duty_costs.table.active'),
      width: 5,
    },
  ];

  const rows: TableRow[] = filtered.map((duty) => ({
    id: { value: duty.id },
    htsNumber: {
      value: (
        <span className="font-mono text-xs whitespace-nowrap">
          {duty.htsNumber}
        </span>
      ),
    },
    description: {
      value: (
        <span className="text-sm text-neut-prim">
          {duty.description ?? '—'}
        </span>
      ),
    },
    origin: { value: duty.originCountryCode ?? '—' },
    adValoremRate: { value: formatRate(duty.dutyAdValoremRate) },
    specificRate: {
      value:
        duty.dutySpecificRate != null
          ? `${duty.dutySpecificRate} ${duty.dutySpecificUnit ?? ''}`.trim()
          : '—',
    },
    section301Rate: { value: formatRate(duty.section301Rate) },
    adCvdRate: {
      value:
        duty.adCvdRate != null
          ? `${formatRate(duty.adCvdRate)}${duty.adCvdType ? ` (${duty.adCvdType})` : ''}`
          : '—',
    },
    gspEligible: { value: <FlagBadge value={duty.gspEligible} /> },
    conditional: { value: <FlagBadge value={duty.conditional} /> },
    active: {
      value: (
        <ActiveBadge
          active={duty.active}
          label={t(`logistics.add_on_costs.active.${duty.active}`)}
        />
      ),
    },
  }));

  return (
    <div className="flex gap-4">
      <div className="flex-1 bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neut-prim">
              {t('logistics.duty_costs.title')}
            </h2>
            <p className="text-xs text-neut-sec mt-1">
              {t('logistics.duty_costs.subtitle')}
            </p>
          </div>
          <Button
            size="sm"
            width="auto"
            onClick={() => setFilterPanelOpen((o) => !o)}
          >
            <Filter size={16} className="mr-1" />
            {t('logistics.common.filter')}
            {activeFilterCount > 0 && (
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 ml-1 text-xs font-semibold rounded-full bg-prim text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table
            minWidth={70}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            emptyMessage={t('logistics.duty_costs.empty')}
          />
          {isLoading && <PageLoadingSpinner name="duty-costs" />}
        </div>
      </div>

      {filterPanelOpen && (
        <FilterSidebar
          title={t('logistics.common.filter')}
          onClose={() => setFilterPanelOpen(false)}
          onApply={applyFilter}
          onClear={clearFilter}
        >
          <Input
            label={t('logistics.duty_costs.filter_search')}
            name="search"
            value={pendingSearch}
            onChange={(_name, value) => setPendingSearch(value)}
          />

          <Input
            label={t('logistics.duty_costs.table.origin')}
            name="origin"
            value={pendingOrigin}
            onChange={(_name, value) => setPendingOrigin(value)}
          />

          <SearchableSelect
            label={t('logistics.duty_costs.table.active')}
            name="active"
            value={pendingActive}
            onChange={(_name, value) => setPendingActive(value)}
            options={[
              { value: '', label: '—' },
              {
                value: 'true',
                label: t('logistics.add_on_costs.active.true'),
              },
              {
                value: 'false',
                label: t('logistics.add_on_costs.active.false'),
              },
            ]}
          />
        </FilterSidebar>
      )}
    </div>
  );
}
